import type { ApiCreateNutritionPlanDto } from '@mfw/common/ui/dtos';

import { getLocalizedNameByLocale } from '@mfw/components';

import type {
  UiNutritionPlanTranslation,
  ApiNutritionPlan,
} from '../../schemas/nutritionPlans.schema';
import type { CreateNutritionPlanArgs } from '../types/nutritionPlans.types';
import type { Locale } from '../../../../@mfw-translations';

import { mapTranslationsFromApi } from './translations.mappers';
import { getLabelFromTranslations } from '../../utils/locales';

export function createNutritionPlanApiArgs(
  data: Omit<
    CreateNutritionPlanArgs,
    'coverSource' | 'locale' | 'organizationId'
  > & { coverSource?: null | string },
) {
  const tags: string[] = [];
  if (data.categories && Object.keys(data.categories).length) {
    Object.values(data.categories).forEach((category) => {
      category?.forEach((tag) => tags.push(tag.value));
    });
  }
  const weeks = (data.weeks ?? []).map((week, idx) => ({
    mealDays: week.mealDays?.flatMap((mealDay) => ({
      day: mealDay.day,
      mealId: mealDay.meal.id,
      mealTime: mealDay.mealTime,
      mealType: mealDay.mealType,
    })),
    weekIndex: idx,
    weekName: week.weekName,
  }));

  const marketplaceConfig =
    data.isMarketplace !== undefined
      ? {
          marketplace: {
            enabled: data.isMarketplace,
            price: data.marketplacePrice ?? 0,
          },
        }
      : {};
  return {
    ...(data.isOrg !== undefined && {
      configuration: { isOrg: data.isOrg, ...marketplaceConfig },
    }),
    coverSource: data.coverSource,
    coverType: data.coverType,
    tags,
    translations: data.translations.map((t) => ({
      description: t.description,
      locale: t.locale?.[0]
        ?.value as ApiCreateNutritionPlanDto['translations'][number]['locale'],
      name: t.name,
    })),
    weeks,
  };
}

export const mapNutritionPlanFromApi = (
  plan: ApiNutritionPlan,
  locale: string,
) => {
  const categories: Record<string, { label: string; value: string }[]> = {};
  if (plan.categories?.length) {
    plan.categories.forEach((category) => {
      categories[category.id] = (category.tags ?? []).map((tag) => ({
        label: getLabelFromTranslations(
          locale as Locale,
          mapTranslationsFromApi(tag.translations),
        ),
        value: tag.id,
      }));
    });
  }

  return {
    ...plan,
    categories,
    configuration: {
      isMarketplace: plan.configuration?.marketplace.enabled ?? false,
      isOrg: plan.configuration?.isOrg ?? false,
      marketplaceCurrency: plan.configuration?.marketplace.currency,
      marketplaceId: plan.configuration?.marketplace.id ?? null,
      marketplaceInLibrary: plan.configuration?.marketplace.inLibrary ?? false,
      marketplaceRestorable:
        plan.configuration?.marketplace.restorable ?? false,
      marketplaceLibraryId: plan.configuration?.marketplace.libraryId ?? null,
      marketplacePrice: plan.configuration?.marketplace.enabled
        ? (plan.configuration?.marketplace.price ?? 0)
        : undefined,
    },
    coverSignedUrl: plan.coverSignedUrl ?? null,
    // the server decided the teaser and already stripped the payload
    previewOnly: plan.previewOnly ?? false,
    translations: (plan.translations ?? []).map(
      (t): UiNutritionPlanTranslation => ({
        description: t.description ?? '',
        locale: [
          { label: getLocalizedNameByLocale(t.locale), value: t.locale },
        ],
        name: t.name,
      }),
    ),
    uniqueId: plan.id,
  };
};
